/**
 * Step configuration for the insurance connector flow.
 *
 * Defines which states show a loading bar and what navigation
 * buttons to render. The parent layout renders these elements
 * OUTSIDE the AnimatePresence boundary so they stay stable
 * across step transitions.
 */

import type { TranslationNamespaceKeys } from "@frontend/lib/next-intl-typed";
import type { InsuranceConnectorEvent } from "./machine/types";

export const INSURANCE_STEP_FORM_ID = "insurance-step-form";

type SimpleInsuranceEventType = Exclude<
  InsuranceConnectorEvent,
  { data: unknown } | { allOON: unknown }
>["type"];

type InsuranceConnectorKey = TranslationNamespaceKeys<"InsuranceConnector">;

export type StepAction =
  | {
      type: "submit";
      label: InsuranceConnectorKey;
      variant?: "default" | "outline";
    }
  | {
      type: "event";
      label: InsuranceConnectorKey;
      event: SimpleInsuranceEventType;
      variant?: "default" | "outline";
    };

export type StepConfig = {
  showLoadingBar: boolean;
  showBackButton: boolean;
  actions: StepAction[];
};

export function getStepConfig(fullState: string): StepConfig | null {
  switch (fullState) {
    case "nameInput":
    case "birthdayInput":
    case "phoneNumberInput":
    case "insurancePayer":
    case "memberIdInput":
    case "insuredStateInput":
    case "secondaryPayerInput":
    case "secondaryMemberIdInput":
    case "secondaryInsuredStateInput":
      return {
        showLoadingBar: true,
        showBackButton: true,
        actions: [{ type: "submit", label: "Next" }],
      };

    case "otpInput":
      return {
        showLoadingBar: true,
        showBackButton: true,
        actions: [{ type: "submit", label: "Verify" }],
      };

    case "disambiguationQuestion":
      return {
        showLoadingBar: false,
        showBackButton: true,
        actions: [{ type: "submit", label: "Continue" }],
      };

    case "secondaryQuestion":
      return {
        showLoadingBar: true,
        showBackButton: true,
        actions: [
          {
            type: "event",
            label: "SkipForNow",
            event: "NO_SECONDARY",
            variant: "outline",
          },
          { type: "event", label: "Yes", event: "YES_SECONDARY" },
        ],
      };

    case "unverifiedRetryName":
    case "unverifiedRetryMemberId":
    case "unverifiedRetryInsuredState":
      return {
        showLoadingBar: false,
        showBackButton: true,
        actions: [{ type: "submit", label: "LooksGood" }],
      };

    case "unverifiedRetryPayer":
      return {
        showLoadingBar: false,
        showBackButton: false,
        actions: [{ type: "submit", label: "LooksGood" }],
      };

    default:
      return null;
  }
}
